/**
 * A nota da consulta pela IA na demonstração.
 *
 * Nenhuma IA é chamada e nenhum áudio sai do navegador: a "transcrição" e a
 * "sugestão" são frases fixas que dizem ser de demonstração. As regras são as
 * do banco (0155, 0156), para a tela e os testes exercitarem o fluxo inteiro.
 */

import type { ModoDaNota, NotaDeIa, OpcoesDaNovaNota, ParteDaNota, ResumoDaConsulta } from './notaDeIa';
import { notaEmAndamento } from './notaDeIa';
import type { PortaDeDados } from './porta';
import type { CampoDeFicha, FichaPreenchida, ModeloDeFicha, NovaFicha, RespostaDaFicha, SituacaoDoAtendimento } from './tipos';

type MetodosDaNota = Pick<
    PortaDeDados,
    | 'notasDeIaDaConsulta' | 'iniciarNotaDeIa' | 'enviarParteDaNotaDeIa' | 'partesDaNotaDeIa'
    | 'gerarNotaDeIa' | 'aprovarNotaDeIa' | 'descartarNotaDeIa' | 'resumosDaIaDoPaciente'
>;

export interface DependenciasDaNotaNaDemo {
    situacaoDoAtendimento: (atendimentoId: string) => SituacaoDoAtendimento | undefined;
    contatoDoAtendimento: (atendimentoId: string) => string | undefined;
    modelos: () => ModeloDeFicha[];
    criarModelo: (nome: string, campos: CampoDeFicha[]) => ModeloDeFicha;
    salvarFicha: (nova: NovaFicha) => Promise<FichaPreenchida>;
    pode: (permissao: string) => boolean;
    papel: () => string | undefined;
    nomeDeQuemUsa: () => string | undefined;
    preferencia: (chave: string) => unknown;
}

interface NotaGuardada extends NotaDeIa {
    autorEmail: string;
    partes: ParteDaNota[];
}

export const NOME_DO_MODELO_DA_NOTA = 'Nota da consulta';

/** Os rótulos da `app.modelo_da_nota_de_ia` (0155). */
export const CAMPOS_DA_NOTA_PADRAO: CampoDeFicha[] = [
    { uid: 'nq', tipo: 'texto-longo', rotulo: 'Queixa principal' },
    { uid: 'nh', tipo: 'texto-longo', rotulo: 'História' },
    { uid: 'ne', tipo: 'texto-longo', rotulo: 'Exame' },
    { uid: 'na', tipo: 'texto-longo', rotulo: 'Avaliação' },
    { uid: 'nc', tipo: 'texto-longo', rotulo: 'Conduta' },
];

let notas: NotaGuardada[] = [];
let sequencia = 0;

export function reiniciarNotasDaDemonstracao(): void {
    notas = [];
    sequencia = 0;
}

function copia(n: NotaGuardada): NotaDeIa {
    const nota: NotaDeIa & Partial<Pick<NotaGuardada, 'autorEmail' | 'partes'>> = { ...n };
    delete nota.autorEmail;
    delete nota.partes;
    return { ...nota, ...(nota.sugestao ? { sugestao: { ...nota.sugestao } } : {}) };
}

export function notaDeIaNaDemonstracao(dep: DependenciasDaNotaNaDemo, quem: () => string | undefined): MetodosDaNota {
    const exigirAtender = () => {
        if (!dep.pode('atendimentos')) throw new Error('Esta ação exige a permissão de atendimento.');
    };
    const minha = (notaId: string): NotaGuardada => {
        const n = notas.find((x) => x.id === notaId);
        if (!n || n.autorEmail !== quem()) throw new Error('Nota não encontrada, ou sem permissão.');
        return n;
    };

    return {
        async notasDeIaDaConsulta(atendimentoId: string): Promise<NotaDeIa[]> {
            return notas
                .filter((n) => n.atendimentoId === atendimentoId
                    && (n.autorEmail === quem() || (n.situacao === 'aprovada' && dep.pode('atendimentos'))))
                .sort((a, b) => b.criadoEm.localeCompare(a.criadoEm))
                .map(copia);
        },

        async iniciarNotaDeIa(atendimentoId: string, modo: ModoDaNota, opcoes: OpcoesDaNovaNota): Promise<NotaDeIa> {
            exigirAtender();
            if (dep.situacaoDoAtendimento(atendimentoId) !== 'em-atendimento') throw new Error('A nota da IA só se grava com a consulta aberta.');
            if (modo === 'consulta' && !opcoes.consentimento) throw new Error('Gravar a consulta exige o consentimento do paciente.');
            const contatoId = dep.contatoDoAtendimento(atendimentoId);
            if (!contatoId) throw new Error('Consulta não encontrada.');
            const minhas = notas.filter((n) => n.atendimentoId === atendimentoId && n.autorEmail === quem());
            if (notaEmAndamento(minhas)) throw new Error('Já há uma nota da IA em andamento nesta consulta. Conclua ou descarte antes de gravar outra.');

            let modeloId = opcoes.modeloId;
            if (modeloId) {
                if (!dep.modelos().some((m) => m.id === modeloId)) throw new Error('Modelo de ficha não encontrado.');
            } else {
                modeloId = (dep.modelos().find((m) => m.nome === NOME_DO_MODELO_DA_NOTA)
                    ?? dep.criarModelo(NOME_DO_MODELO_DA_NOTA, CAMPOS_DA_NOTA_PADRAO)).id;
            }

            sequencia += 1;
            const agora = new Date().toISOString();
            const nota: NotaGuardada = {
                id: `nota-ia-${sequencia}`,
                atendimentoId, contatoId, modo, modeloId,
                situacao: 'gravando',
                criadoEm: agora,
                ...(modo === 'consulta' ? { consentimentoEm: agora } : {}),
                ...(dep.nomeDeQuemUsa() ? { autor: dep.nomeDeQuemUsa() } : {}),
                autorEmail: quem() ?? '',
                partes: [],
            };
            notas.push(nota);
            return copia(nota);
        },

        async enviarParteDaNotaDeIa(notaId, parte): Promise<void> {
            const n = minha(notaId);
            if (n.situacao !== 'gravando') throw new Error('A gravação desta nota já foi encerrada.');
            if (parte.audio.size === 0) throw new Error('A parte do áudio ficou vazia.');
            n.partes = [
                ...n.partes.filter((p) => p.ordem !== parte.ordem),
                { ordem: parte.ordem, segundos: parte.segundos, texto: `Transcrição de demonstração, parte ${parte.ordem + 1}.` },
            ];
        },

        async partesDaNotaDeIa(notaId: string): Promise<ParteDaNota[]> {
            const n = notas.find((x) => x.id === notaId);
            if (!n || !(n.autorEmail === quem() || (n.situacao === 'aprovada' && dep.pode('atendimentos')))) return [];
            return [...n.partes].sort((a, b) => a.ordem - b.ordem).map((p) => ({ ...p }));
        },

        async gerarNotaDeIa(notaId: string): Promise<NotaDeIa> {
            const n = minha(notaId);
            if (!['gravando', 'falhou', 'revisao'].includes(n.situacao)) throw new Error('Esta nota não pode ser gerada agora.');
            if (n.partes.length === 0) {
                n.situacao = 'falhou';
                n.erro = 'Nada foi transcrito: grave de novo, perto do microfone.';
                return copia(n);
            }
            const modelo = dep.modelos().find((m) => m.id === n.modeloId);
            const primeiroTexto = modelo?.campos.find((c) => c.tipo === 'texto-longo' || c.tipo === 'texto');
            const sugestao: Record<string, RespostaDaFicha> = {};
            if (primeiroTexto) sugestao[primeiroTexto.uid] = 'Sugestão de demonstração — sem IA.';
            n.sugestao = sugestao;
            n.resumoSugerido = 'Resumo de demonstração — nenhuma IA foi chamada.';
            n.situacao = 'revisao';
            delete n.erro;
            return copia(n);
        },

        async aprovarNotaDeIa(notaId, respostas, resumo): Promise<string> {
            exigirAtender();
            const n = minha(notaId);
            if (n.situacao !== 'revisao') throw new Error('Só a nota pronta para revisão pode ser aprovada.');
            if (Object.keys(respostas).length === 0) throw new Error('A nota aprovada precisa de ao menos um campo preenchido.');
            if (!resumo.trim()) throw new Error('A nota aprovada precisa do resumo da consulta.');
            if (!n.modeloId) throw new Error('O modelo desta nota foi excluído. Descarte a nota e grave de novo.');
            const ficha = await dep.salvarFicha({ atendimentoId: n.atendimentoId, contatoId: n.contatoId, modeloId: n.modeloId, respostas });
            n.situacao = 'aprovada';
            n.fichaId = ficha.id;
            n.resumo = resumo.trim();
            n.decididaEm = new Date().toISOString();
            return ficha.id;
        },

        async descartarNotaDeIa(notaId: string): Promise<void> {
            const n = minha(notaId);
            if (n.situacao === 'aprovada' || n.situacao === 'descartada') throw new Error('Esta nota já foi decidida.');
            n.situacao = 'descartada';
            n.partes = [];
            delete n.sugestao;
            delete n.resumoSugerido;
            n.decididaEm = new Date().toISOString();
        },

        async resumosDaIaDoPaciente(contatoId: string): Promise<ResumoDaConsulta[]> {
            const escolha = String(dep.preferencia('at.resumo-ia') ?? 'profissional');
            const papel = dep.papel();
            const visivel = papel === 'proprietaria' || dep.pode('*')
                || (papel === 'profissional' && (escolha === 'profissional' || escolha === 'todos'))
                || (papel === 'recepcao' && escolha === 'todos');
            return notas
                .filter((n) => n.contatoId === contatoId && n.situacao === 'aprovada')
                .sort((a, b) => (b.decididaEm ?? '').localeCompare(a.decididaEm ?? ''))
                .map((n) => ({
                    notaId: n.id,
                    atendimentoId: n.atendimentoId,
                    modo: n.modo,
                    aprovadoEm: n.decididaEm ?? n.criadoEm,
                    visivel,
                    ...(n.autor ? { autor: n.autor } : {}),
                    ...(visivel && n.resumo ? { resumo: n.resumo } : {}),
                }));
        },
    };
}
